import React, { useState } from 'react';
import { Shield, Eye, EyeOff, AlertCircle, Mail, CheckCircle, ArrowLeft } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

export default function AdminAuth({ onAuthSuccess }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);
    try {
      if (mode === 'forgot') {
        const { error: resetErr } = await supabase?.auth?.resetPasswordForEmail(form?.email, {
          redirectTo: `${window.location?.origin}/admin-management-dashboard?reset=true`,
        });
        if (resetErr) {
          setError(resetErr?.message);
        } else {
          setSuccessMsg('Password reset email sent! Check your inbox.');
        }
        return;
      }

      const { data, error: authErr } = await supabase?.auth?.signInWithPassword({
        email: form?.email,
        password: form?.password,
      });
      if (authErr) { setError(authErr?.message); return; }

      const user = data?.user;
      const role = user?.raw_user_meta_data?.role || user?.user_metadata?.role || user?.app_metadata?.role;
      if (role !== 'admin') {
        setError('Access denied. Admin privileges required.');
        await supabase?.auth?.signOut();
        return;
      }
      onAuthSuccess(user);
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccessMsg('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#0D1B2A' }}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #7B2FBE, transparent 70%)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full opacity-8" style={{ background: 'radial-gradient(circle, #00E5FF, transparent 70%)' }} />
      </div>
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4" style={{ background: 'linear-gradient(135deg, rgba(123,47,190,0.2), rgba(0,229,255,0.15))', border: '1px solid rgba(123,47,190,0.4)' }}>
            <Shield size={24} style={{ color: '#7B2FBE' }} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>Admin Console</h1>
          <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>
            {mode === 'login' ? 'Restricted access — authorized personnel only' : 'Reset your admin password'}
          </p>
        </div>

        <div className="rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(123,47,190,0.2)', backdropFilter: 'blur(20px)' }}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Admin Email</label>
              <input
                type="email"
                value={form?.email}
                onChange={(e) => setForm(f => ({ ...f, email: e?.target?.value }))}
                placeholder="admin@finvisar.com"
                required
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(123,47,190,0.25)', fontFamily: 'DM Sans, sans-serif' }}
              />
            </div>

            {mode === 'login' && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Password</label>
                  <button
                    type="button"
                    onClick={() => switchMode('forgot')}
                    className="text-xs transition-colors"
                    style={{ color: '#7B2FBE', fontFamily: 'DM Sans, sans-serif' }}
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={form?.password}
                    onChange={(e) => setForm(f => ({ ...f, password: e?.target?.value }))}
                    placeholder="••••••••"
                    required
                    className="w-full px-4 py-3 pr-11 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                    style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(123,47,190,0.25)', fontFamily: 'DM Sans, sans-serif' }}
                  />
                  <button type="button" onClick={() => setShowPass(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }}>
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <AlertCircle size={14} style={{ color: '#EF4444' }} />
                <p className="text-xs" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>{error}</p>
              </div>
            )}

            {successMsg && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
                <CheckCircle size={14} style={{ color: '#10B981' }} />
                <p className="text-xs" style={{ color: '#10B981', fontFamily: 'DM Sans, sans-serif' }}>{successMsg}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 mt-2 flex items-center justify-center gap-2"
              style={{ background: loading ? 'rgba(123,47,190,0.4)' : 'linear-gradient(135deg, #7B2FBE, #00E5FF)', fontFamily: 'DM Sans, sans-serif' }}
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : mode === 'forgot' ? (
                <Mail size={15} />
              ) : null}
              {loading ? 'Processing...' : mode === 'login' ? 'Access Admin Console' : 'Send Reset Link'}
            </button>
          </form>

          {mode === 'forgot' && (
            <button
              onClick={() => switchMode('login')}
              className="w-full mt-4 flex items-center justify-center gap-1.5 text-xs transition-colors"
              style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}
            >
              <ArrowLeft size={13} />
              Back to sign in
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
